<template>
  <Teleport to="body">
    <div v-if="open" ref="panel" class="dash-popover" :style="style" @click.stop @mousedown.stop>
      <slot></slot>
    </div>
  </Teleport>
</template>

<script>
/**
 * Small anchored popover (menus, pickers). Pass the anchor element and
 * control `open` from the parent; the popover emits `close` on outside
 * click, Escape, scroll or resize.
 */
export default {
  name: 'DashPopover',
  props: {
    open: { type: Boolean, default: false },
    anchor: { type: null, default: null },
    align: { type: String, default: 'left' },
    minWidth: { type: Number, default: 180 },
  },
  emits: ['close'],
  data() {
    return { style: { position: 'fixed', top: '0px', left: '0px', visibility: 'hidden' } };
  },
  watch: {
    open(val) {
      if (val) {
        this.style = { position: 'fixed', top: '0px', left: '0px', visibility: 'hidden', minWidth: `${this.minWidth}px` };
        this.$nextTick(() => {
          this.place();
          this.bind();
        });
      } else {
        this.unbind();
      }
    },
  },
  methods: {
    place() {
      const el = this.anchor;
      const panel = this.$refs.panel;
      if (!el || !panel) return;
      const rect = el.getBoundingClientRect();
      const width = panel.offsetWidth || this.minWidth;
      const height = panel.offsetHeight || 0;
      let left = this.align === 'right' ? rect.right - width : rect.left;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      let top = rect.bottom + 4;
      if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 4);
      this.style = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        minWidth: `${this.minWidth}px`,
        visibility: 'visible',
      };
    },
    onDocumentDown(event) {
      if (this.anchor && this.anchor.contains(event.target)) return;
      if (this.$refs.panel && this.$refs.panel.contains(event.target)) return;
      this.$emit('close');
    },
    onKey(event) {
      if (event.key === 'Escape') this.$emit('close');
    },
    onScroll(event) {
      if (this.$refs.panel && event.target && this.$refs.panel.contains(event.target)) return;
      this.$emit('close');
    },
    bind() {
      document.addEventListener('mousedown', this.onDocumentDown);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('scroll', this.onScroll, true);
      window.addEventListener('resize', this.onScroll);
    },
    unbind() {
      document.removeEventListener('mousedown', this.onDocumentDown);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('scroll', this.onScroll, true);
      window.removeEventListener('resize', this.onScroll);
    },
  },
  beforeUnmount() {
    this.unbind();
  },
};
</script>
